/**
 * FlowCanvasEventHandler - Centralizes all canvas event handling logic
 *
 * Responsibilities:
 * - Process all canvas events (node moved, created, deleted, etc.)
 * - Coordinate state updates with minimal re-renders
 * - Emit EventBus events for tracking
 * - Update ScreenRegistry and Coordinator
 *
 * Architecture:
 * - Single entry point: handleEvent() with switch/case
 * - Separate handler function for each event type
 * - Optimized for performance: only updates affected entities
 *
 * Event Flow:
 * Canvas detects user action → ReactFlowCanvas emits callback →
 * FlowCanvasEventHandler processes → Updates Registry/State → Emits EventBus event
 *
 * Sprint: sprint-2
 * Task: T002.14
 */

import type { Screen, FlowEntity } from '@modules/reactFlow/types/flow-canvas-types';
import type { FlowCoordinator } from '@modules/reactFlow/services/shared/FlowCoordinator';
import type { CommandManager } from '@modules/reactFlow/commands/CommandManager';
import {
  CreateEntityCommand,
  DeleteEntityCommand,
  MoveEntityCommand,
  CreateEdgeCommand,
  DeleteEdgeCommand,
  EditEdgeCommand,
  SetLaunchEntityCommand
} from '@modules/reactFlow/commands';
import { CloneEntityCommand } from '@modules/reactFlow/commands/CloneEntityCommand';
import { BLOCK_DIMENSIONS } from '@modules/reactFlow/constants/blockDimensionConfig';

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

/**
 * Context passed to event handlers containing all necessary state and callbacks
 *
 * FR-P1-003: NO screenRegistry, NO screenService - coordinator only
 */
export interface EventHandlerContext {
  // Services (FR-P1-003: coordinator only)
  // Supports V2 screen and block coordinators through the generic coordinator contract.
  coordinator: FlowCoordinator<FlowEntity, unknown>;

  // State updaters
  setSelectedScreenIds: React.Dispatch<React.SetStateAction<string[]>>;

  // CommandManager - accessed via getter from coordinator
  readonly commandManager: CommandManager;

  // Logging
  logEvent: (message: string) => void;

  // Loading indicators
  setIsSyncing: React.Dispatch<React.SetStateAction<boolean>>;
  setSyncOperation: React.Dispatch<React.SetStateAction<string>>;

  // Confirmation dialogs
  showConfirmation?: (message: string, details?: string) => Promise<boolean>;
}

/**
 * Supported canvas events
 * Sprint-5: Added edge.clicked and edge.updated for TransitionEditor
 * Sprint-12: Added block.* events for StrategyBlock support
 */
export type FlowCanvasEventType =
  | 'screen.selected'
  | 'screen.moved'
  | 'screen.created'
  | 'screen.deleted'
  | 'block.selected'   // Sprint-12: Block selection
  | 'block.moved'      // Sprint-12: Block position update
  | 'block.created'    // Sprint-12: Block creation
  | 'block.deleted'    // Sprint-12: Block deletion
  | 'node.action'      // Node toolbar action (edit, delete, copy, etc)
  | 'edge.created'
  | 'edge.deleted'
  | 'edge.clicked'     // Sprint-5: User clicked on edge (double-click or info button)
  | 'edge.updated';    // Sprint-5: User updated edge properties (transition kind)

/**
 * Base event structure
 */
export interface FlowCanvasEventBase {
  type: FlowCanvasEventType;
  timestamp: number;
}

/**
 * Event payloads for each event type
 */
export interface ScreenSelectedEventPayload extends FlowCanvasEventBase {
  type: 'screen.selected';
  screenIds: string[];
  additive: boolean;
}

export interface ScreenMovedEventPayload extends FlowCanvasEventBase {
  type: 'screen.moved';
  movedScreens: Array<{ screenId: string; newPosition: { x: number; y: number } }>;
}

export interface ScreenCreatedEventPayload extends FlowCanvasEventBase {
  type: 'screen.created';
  templateId: string;
  position: { x: number; y: number };
}

export interface ScreenDeletedEventPayload extends FlowCanvasEventBase {
  type: 'screen.deleted';
  screenIds: string[];
}

export interface EdgeCreatedEventPayload extends FlowCanvasEventBase {
  type: 'edge.created';
  sourceEntityId: string;
  targetEntityId: string;
  sourceActionKey: string;
}

export interface EdgeDeletedEventPayload extends FlowCanvasEventBase {
  type: 'edge.deleted';
  edgeIds: string[];
}

// Sprint-5: New event payloads for TransitionEditor
export interface EdgeClickedEventPayload extends FlowCanvasEventBase {
  type: 'edge.clicked';
  edgeId: string;
  edgeData: any;
}

export interface EdgeUpdatedEventPayload extends FlowCanvasEventBase {
  type: 'edge.updated';
  edgeId: string;
  updates: {
    transitionKind?: 'Default' | 'Conditional';
  };
}

// Sprint-12: Block event payloads
export interface BlockSelectedEventPayload extends FlowCanvasEventBase {
  type: 'block.selected';
  blockIds: string[];
  additive: boolean;
}

export interface BlockMovedEventPayload extends FlowCanvasEventBase {
  type: 'block.moved';
  movedBlocks: Array<{ blockId: string; newPosition: { x: number; y: number } }>;
}

export interface BlockCreatedEventPayload extends FlowCanvasEventBase {
  type: 'block.created';
  blockData: any;
  position: { x: number; y: number };
}

export interface BlockDeletedEventPayload extends FlowCanvasEventBase {
  type: 'block.deleted';
  blockIds: string[];
}

export interface NodeActionEventPayload extends FlowCanvasEventBase {
  type: 'node.action';
  entityId: string;
  actionKey: string;
  entityType?: 'block' | 'screen';
}

export type FlowCanvasEvent =
  | ScreenSelectedEventPayload
  | ScreenMovedEventPayload
  | ScreenCreatedEventPayload
  | ScreenDeletedEventPayload
  | BlockSelectedEventPayload   // Sprint-12
  | BlockMovedEventPayload      // Sprint-12
  | BlockCreatedEventPayload    // Sprint-12
  | BlockDeletedEventPayload    // Sprint-12
  | NodeActionEventPayload      // Node toolbar actions
  | EdgeCreatedEventPayload
  | EdgeDeletedEventPayload
  | EdgeClickedEventPayload     // Sprint-5
  | EdgeUpdatedEventPayload;    // Sprint-5

/**
 * Centralized event handler for all canvas events
 */
export class FlowCanvasEventHandler {
  /**
   * Main event dispatcher - routes events to specific handlers
   *
   * @param event - The canvas event to process
   * @param context - Context with state updaters and utilities
   * @returns Promise that resolves when event handling completes (for async operations)
   */
  public async handleEvent(event: FlowCanvasEvent, context: EventHandlerContext): Promise<void> {
    switch (event.type) {
      case 'screen.selected':
        this.handleScreenSelected(event, context);
        break;

      case 'screen.moved':
        await this.handleScreenMoved(event, context);
        break;

      case 'screen.created':
        await this.handleScreenCreated(event, context);
        break;

      case 'screen.deleted':
        await this.handleScreenDeleted(event, context);
        break;

      case 'block.selected':
        this.handleBlockSelected(event, context);
        break;

      case 'block.moved':
        await this.handleBlockMoved(event, context);
        break;

      case 'block.created':
        await this.handleBlockCreated(event, context);
        break;

      case 'block.deleted':
        await this.handleBlockDeleted(event, context);
        break;

      case 'node.action':
        await this.handleNodeAction(event, context);
        break;

      case 'edge.created':
        await this.handleEdgeCreated(event, context);
        break;

      case 'edge.deleted':
        await this.handleEdgeDeleted(event, context);
        break;

      case 'edge.clicked':
        this.handleEdgeClicked(event, context);
        break;

      case 'edge.updated':
        await this.handleEdgeUpdated(event, context);
        break;

      default:
        console.warn('[FlowCanvasEventHandler] Unknown event type:', (event as any).type);
    }
  }

  /**
   * Handle screen selection
   * Updates: selectedScreenIds state only
   * Re-renders: None (selection handled by ReactFlow internally)
   */
  private handleScreenSelected(
    event: ScreenSelectedEventPayload,
    context: EventHandlerContext
  ): void {
    const { screenIds } = event;

    // Update selection state
    context.setSelectedScreenIds(screenIds);
    context.logEvent(`Screens selected: ${screenIds.join(', ')}`);
  }

  /**
   * Handle screen movement
   * Uses MoveScreenCommand for automatic rollback and undo support
   *
   * OPTIMIZATION: Does NOT update screens state - ReactFlow manages positions internally
   * Command only updates Registry and Firebase
   * Re-renders: NONE - positions handled by ReactFlow
   *
   * Firebase sync: If screenService provided, updates position in Firebase with error handling
   */
  private async handleScreenMoved(
    event: ScreenMovedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { movedScreens } = event;

    console.log('[FlowCanvasEventHandler] 🚀 handleScreenMoved called with:', {
      count: movedScreens.length,
      screens: movedScreens.map(s => ({
        id: s.screenId.slice(0, 8),
        pos: s.newPosition
      }))
    });

    try {
      // Capture old positions from Coordinator before creating command (FR-P1-003)
      const movesWithOldPositions = movedScreens.map(({ screenId, newPosition }) => {
        const screen = context.coordinator.getEntity(screenId);
        const oldPosition = screen?.position || { x: 0, y: 0 };
        console.log(`[FlowCanvasEventHandler] 📍 Screen ${screenId.slice(0, 8)}: ${JSON.stringify(oldPosition)} → ${JSON.stringify(newPosition)}`);
        return {
          entityId: screenId,
          oldPosition,
          newPosition
        };
      });

      // Create command with moves array (supports batch movement) (FR-P1-003: coordinator only)
      const command = new MoveEntityCommand(movesWithOldPositions, {
        coordinator: context.coordinator,
        commandManager: context.commandManager,
        setSelectedScreenIds: context.setSelectedScreenIds,
        setIsSyncing: context.setIsSyncing,
        setSyncOperation: context.setSyncOperation,
        logEvent: context.logEvent,
      });

      // Execute command through CommandManager
      const result = await context.commandManager.execute(command);

      if (!result.success) {
        context.logEvent(`Error: ${result.error?.message || 'Failed to move screens'}`);
      }
    } catch (error) {
      console.error('Failed to move screens:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // NOTE: We do NOT call setScreens() here - this prevents re-rendering all nodes
    // ReactFlow manages node positions internally via onNodesChange
  }

  /**
   * Handle new screen creation from template
   * Uses CreateScreenCommand for automatic rollback and undo support
   *
   * Updates: Adds single screen to state
   * Re-renders: Only the new node
   *
   * Firebase sync: If screenService provided, saves to Firebase with error handling
   */
  private async handleScreenCreated(
    event: ScreenCreatedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { templateId, position } = event;

    console.log('🎬 [EventHandler] handleScreenCreated called');
    console.log('  - templateId:', templateId);
    console.log('  - position:', position);
    console.log('  - context.coordinator exists:', !!context.coordinator);
    console.log('  - context.commandManager exists:', !!context.commandManager);

    try {
      // Create command with command context (FR-P1-003: coordinator only)
      // templateId is pass-through creation data consumed by the entity service,
      // not a persisted FlowEntity field — hence the cast.
      const command = new CreateEntityCommand({ templateId } as Partial<FlowEntity>, position, {
        coordinator: context.coordinator,
        commandManager: context.commandManager,
        setSelectedScreenIds: context.setSelectedScreenIds,
        setIsSyncing: context.setIsSyncing,
        setSyncOperation: context.setSyncOperation,
        logEvent: context.logEvent,
      });

      console.log('  ✅ Command created successfully');

      // Execute command through CommandManager
      const result = await context.commandManager.execute(command);

      console.log('  ✅ Command executed:', result.success ? 'SUCCESS' : 'FAILED');
      if (result.affectedEntities?.screens) {
        console.log('  - Created screen IDs:', result.affectedEntities.screens);
      }

      if (result.success) {
        const screenId = result.affectedEntities.screens![0];
        context.logEvent(`New screen created from template at (${position.x}, ${position.y})`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to create screen'}`);
      }
    } catch (error) {
      console.error('Failed to create screen:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle screen deletion
   * Uses DeleteScreenCommand for automatic rollback and undo support
   *
   * Updates: Removes only deleted screens and connected edges
   * Re-renders: None for remaining nodes
   *
   * Firebase sync: If screenService provided, deletes from Firebase with error handling
   */
  private async handleScreenDeleted(
    event: ScreenDeletedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const screenIds = uniqueIds(event.screenIds);
    if (screenIds.length === 0) return;

    try {
      // Create command with command context (FR-P1-003: coordinator only)
      const command = new DeleteEntityCommand(screenIds, {
        coordinator: context.coordinator,
        commandManager: context.commandManager,
        setSelectedScreenIds: context.setSelectedScreenIds,
        setIsSyncing: context.setIsSyncing,
        setSyncOperation: context.setSyncOperation,
        logEvent: context.logEvent,
      });

      // Execute command through CommandManager
      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`Deleted ${screenIds.length} screen(s)`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to delete screens'}`);
      }
    } catch (error) {
      console.error('Failed to delete screens:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle edge (transition) creation
   * Uses CreateEdgeCommand for automatic rollback and undo support
   *
   * Updates: Adds single edge to state
   * Re-renders: None for existing nodes
   *
   * Future: Firebase sync when TransitionService is implemented
   */
  private async handleEdgeCreated(
    event: EdgeCreatedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { sourceEntityId, targetEntityId, sourceActionKey } = event;

    try {
      // Unified approach: use CreateEdgeCommand for both blocks and screens
      const command = new CreateEdgeCommand(
        sourceEntityId,
        targetEntityId,
        sourceActionKey,
        context as any // Cast to any - CreateEdgeCommand uses older CommandContext interface
      );

      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`Transition created: ${sourceEntityId.slice(0, 8)} → ${targetEntityId.slice(0, 8)} via ${sourceActionKey}`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to create edge'}`);
      }
    } catch (error) {
      console.error('Failed to create edge:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle edge (transition) deletion
   * Uses DeleteEdgeCommand for automatic rollback and undo support
   *
   * Updates: Removes only deleted edges
   * Re-renders: None for existing nodes
   *
   * Future: Firebase sync when TransitionService is implemented
   */
  private async handleEdgeDeleted(
    event: EdgeDeletedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const edgeIds = uniqueIds(event.edgeIds);
    if (edgeIds.length === 0) return;

    try {
      // Create command with command context
      const command = new DeleteEdgeCommand(edgeIds, {
        coordinator: context.coordinator,
        commandManager: context.commandManager,
        setSelectedScreenIds: context.setSelectedScreenIds,
        setIsSyncing: context.setIsSyncing,
        setSyncOperation: context.setSyncOperation,
        logEvent: context.logEvent,
      });

      // Execute command through CommandManager
      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`Deleted ${edgeIds.length} edge(s)`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to delete edges'}`);
      }
    } catch (error) {
      console.error('Failed to delete edges:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle edge clicked (Sprint-5: TransitionEditor)
   * Simply logs the event - TransitionEditor dialog is opened by Demo component
   * Updates: None
   * Re-renders: None
   */
  private handleEdgeClicked(
    event: EdgeClickedEventPayload,
    context: EventHandlerContext
  ): void {
    const { edgeId, edgeData } = event;

    context.logEvent(`Edge clicked: ${edgeId}`);
    console.log('[FlowCanvasEventHandler] Edge clicked:', { edgeId, edgeData });
  }

  /**
   * Handle edge updated (Sprint-5: TransitionEditor)
   * Uses EditEdgeCommand for Firebase sync and undo support
   * Updates: transitions state (via coordinator)
   * Re-renders: Minimal (only affected edge)
   */
  private async handleEdgeUpdated(
    event: EdgeUpdatedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { edgeId, updates } = event;

    console.log('[FlowCanvasEventHandler] Updating edge:', { edgeId, updates });

    try {
      context.setIsSyncing(true);
      context.setSyncOperation('Updating transition...');

      // Map transitionKind to label if needed
      const edgeUpdates = {
        label: updates.transitionKind
      };

      // Create and execute EditEdgeCommand
      const command = new EditEdgeCommand(
        edgeId,
        edgeUpdates,
        {
          coordinator: context.coordinator,
          commandManager: context.commandManager,
          setSelectedScreenIds: context.setSelectedScreenIds,
          setIsSyncing: context.setIsSyncing,
          setSyncOperation: context.setSyncOperation,
          logEvent: context.logEvent,
        }
      );

      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`Edge updated: ${edgeId}`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to update edge'}`);
      }
    } catch (error) {
      console.error('Failed to update edge:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      context.setIsSyncing(false);
      context.setSyncOperation('');
    }
  }

  // ============================================================================
  // BLOCK EVENT HANDLERS (Sprint-12)
  // ============================================================================

  /**
   * Handle block selection
   * Sprint-12: Block selection support
   */
  private handleBlockSelected(
    event: BlockSelectedEventPayload,
    context: EventHandlerContext
  ): void {
    const { blockIds, additive } = event;

    if (additive) {
      // Add to existing selection
      context.setSelectedScreenIds(prevIds => {
        const newIds = new Set([...prevIds, ...blockIds]);
        return Array.from(newIds);
      });
    } else {
      // Replace selection
      context.setSelectedScreenIds(blockIds);
    }

    context.logEvent(`Selected ${blockIds.length} block(s)`);
  }

  /**
   * Handle block moved event
   * Sprint-12: Block position update via MoveBlockCommand
   */
  private async handleBlockMoved(
    event: BlockMovedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { movedBlocks } = event;

    try {
      // Convert to EntityMove format with oldPosition from registry
      const entityMoves = movedBlocks.map(({ blockId, newPosition }) => {
        const block = context.coordinator.getEntity(blockId);
        const oldPosition = block?.position || { x: 0, y: 0 };
        return {
          entityId: blockId,
          oldPosition,
          newPosition
        };
      });

      const command = new MoveEntityCommand(entityMoves, context as any);
      await context.commandManager.execute(command);

      context.logEvent(`✅ Moved ${movedBlocks.length} block(s)`);
    } catch (error) {
      console.error('Failed to move blocks:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle block created event
   * Sprint-12: Block creation via CreateBlockCommand
   */
  private async handleBlockCreated(
    event: BlockCreatedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { blockData, position } = event;

    try {
      context.setIsSyncing(true);
      context.setSyncOperation('Creating block...');

      const command = new CreateEntityCommand(
        blockData,
        position,
        context as any
      );

      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`✅ Block created at (${position.x}, ${position.y})`);
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to create block'}`);
      }
    } catch (error) {
      console.error('Failed to create block:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      context.setIsSyncing(false);
      context.setSyncOperation('');
    }
  }

  /**
   * Handle block deleted event
   * Sprint-12: Block deletion via DeleteBlockCommand
   */
  private async handleBlockDeleted(
    event: BlockDeletedEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const blockIds = uniqueIds(event.blockIds);
    if (blockIds.length === 0) return;

    try {
      context.setIsSyncing(true);
      context.setSyncOperation(`Deleting ${blockIds.length} block(s)...`);

      const command = new DeleteEntityCommand(blockIds, context as any);
      const result = await context.commandManager.execute(command);

      if (result.success) {
        context.logEvent(`✅ Deleted ${blockIds.length} block(s)`);
        // Clear selection if deleted blocks were selected
        context.setSelectedScreenIds(prevIds =>
          prevIds.filter(id => !blockIds.includes(id))
        );
      } else {
        context.logEvent(`Error: ${result.error?.message || 'Failed to delete blocks'}`);
      }
    } catch (error) {
      console.error('Failed to delete blocks:', error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      context.setIsSyncing(false);
      context.setSyncOperation('');
    }
  }

  /**
   * Handle node toolbar action
   *
   * Routes action key to appropriate command:
   * - 'edit' → log for demo (TODO: open editor dialog)
   * - 'delete' → DeleteEntityCommand
   * - 'copy' → log for demo (TODO: CopyEntityCommand)
   * - 'set-launch' → log for demo (TODO: UpdateEntityCommand)
   * - 'unset-launch' → log for demo (TODO: UpdateEntityCommand)
   */
  private async handleNodeAction(
    event: NodeActionEventPayload,
    context: EventHandlerContext
  ): Promise<void> {
    const { entityId, actionKey, entityType } = event;

    context.logEvent(`[Node Action] ${actionKey} on ${entityType || 'entity'}: ${entityId}`);

    try {
      switch (actionKey) {
        case 'edit':
          // TODO: Open editor dialog (ScreenEditorDialog or BlockEditorDialog)
          console.log('[EventHandler] Edit action - not implemented yet');
          context.logEvent(`Edit ${entityType || 'entity'}: ${entityId}`);
          break;

        case 'delete':
          // Execute DeleteEntityCommand via CommandManager
          context.setIsSyncing(true);
          context.setSyncOperation(`Deleting ${entityType || 'entity'}...`);

          const deleteCommand = new DeleteEntityCommand([entityId], context as any);
          const result = await context.commandManager.execute(deleteCommand);

          if (result.success) {
            context.logEvent(`✅ Deleted ${entityType || 'entity'}: ${entityId}`);
            // Clear selection
            context.setSelectedScreenIds(prevIds =>
              prevIds.filter(id => id !== entityId)
            );
          } else {
            context.logEvent(`❌ Failed to delete: ${result.error?.message || 'Unknown error'}`);
          }

          context.setIsSyncing(false);
          context.setSyncOperation('');
          break;

        case 'copy':
          // Clone entity with offset positioning
          context.setIsSyncing(true);
          context.setSyncOperation('Cloning entity...');

          try {
            // Validate source entity exists
            const sourceEntity = context.coordinator.getEntity(entityId);
            if (!sourceEntity) {
              throw new Error(`Entity ${entityId} not found`);
            }

            // Calculate offset: place clone to the right of original
            // Offset = BLOCK_WIDTH + BLOCK_WIDTH/3 (562px right, 0px down)
            const offset = {
              x: BLOCK_DIMENSIONS.WIDTH + BLOCK_DIMENSIONS.WIDTH / 3,
              y: 0
            };

            // Create and execute clone command
            const cloneCommand = new CloneEntityCommand(
              entityId,
              offset,
              context as any
            );

            const result = await context.commandManager.execute(cloneCommand);

            if (result.success) {
              context.logEvent(`✅ Cloned ${entityType || 'entity'}: ${entityId.slice(0, 8)}`);
            } else {
              throw new Error(result.error?.message || 'Clone failed');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error('[EventHandler] Clone failed:', errorMessage);
            context.logEvent(`❌ Clone error: ${errorMessage}`);
          } finally {
            context.setIsSyncing(false);
            context.setSyncOperation('');
          }
          break;

        case 'set-launch':
        case 'set-launch-screen': {
          // Check if there are incoming edges that will be deleted
          const allTransitions = context.coordinator.getAllTransitions();
          const incomingEdges = allTransitions.filter(t => t.targetEntityId === entityId);

          // Show confirmation if incoming edges exist
          if (incomingEdges.length > 0) {
            if (!context.showConfirmation) {
              console.error('[EventHandler] showConfirmation callback not provided');
              context.logEvent('Cannot show confirmation - callback missing');
              break;
            }

            const confirmed = await context.showConfirmation(
              'Are you sure you want to set this entity as launch?',
              `Warning: ${incomingEdges.length} incoming edge${incomingEdges.length === 1 ? '' : 's'} will be deleted.`
            );

            if (!confirmed) {
              console.log('[EventHandler] Set launch cancelled by user');
              context.logEvent(`Set launch cancelled: ${entityId}`);
              break;
            }
          }

          // User confirmed (or no edges) - execute command
          const command = new SetLaunchEntityCommand(entityId, context as any);
          const result = await context.commandManager.execute(command);

          if (result.success) {
            const edgeMsg = incomingEdges.length > 0
              ? ` (удалено ${incomingEdges.length} входящих связей)`
              : '';
            const entityType = actionKey === 'set-launch' ? 'block' : 'screen';
            console.log(`[EventHandler] ✅ Set as launch ${entityType}: ${entityId}${edgeMsg}`);
            context.logEvent(`Set as launch ${entityType}: ${entityId}${edgeMsg}`);
          } else {
            console.error('[EventHandler] ❌ Failed to set launch:', result.error);
            context.logEvent(`Failed to set launch: ${result.error?.message}`);
          }
          break;
        }

        default:
          console.warn(`[EventHandler] Unknown action key: ${actionKey}`);
          context.logEvent(`Unknown action: ${actionKey}`);
      }
    } catch (error) {
      console.error(`[EventHandler] Error handling node action ${actionKey}:`, error);
      context.logEvent(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

/**
 * Singleton instance
 */
export const flowCanvasEventHandler = new FlowCanvasEventHandler();
